/**
 * How a NOT-YET-ACTIVE company is presented on the Company information page:
 * the pill that sits on the avatar, and the copy of the empty state that stands
 * in for the package and saldo cards.
 *
 * TWO LIFECYCLES, TWO RESOLVERS, deliberately kept apart:
 *
 *   MEMBERSHIP  (avatarBadgeFor / emptyStateCopyFor) — keyed on memberStatusOf().
 *               The company has finished enrolling and is now somewhere between
 *               "submitted" and "active".
 *   ENROLMENT   (enrolmentPresentation) — keyed on enrollment_step plus the
 *               DocuSign envelope. The company has NOT finished the wizard yet,
 *               so there is no membership status to speak of.
 *
 * Feeding an enrolment state into the membership resolver is the mistake this
 * split exists to prevent: it would tell a company that has never been submitted
 * to anyone that it is "Menunggu verifikasi CLIK" and that its saldo opens once
 * a CLIK review — never requested — finishes.
 *
 * `expired` is deliberately absent from EMPTY_STATE_COPY: an expired company
 * has a real wallet with real money in it, so it keeps the normal cards and
 * only loses the ability to act. Telling it "saldo belum tersedia" would be a
 * lie about its own balance.
 *
 * SOURCE OF THE COPY. The membership strings below are the English rendering
 * of Figma (15564:30150), whose copy is Indonesian — the product UI is English.
 * The enrolment strings are NOT from Figma — the design file has no frame for a
 * mid-enrolment company on this page (checked across all 174 top-level frames
 * of the HR Client Portal canvas). They follow the design's own rule for a
 * blocked state — name the actual blocker on an amber pill — but the wording
 * itself still wants a designer's eye.
 */

import { agreementPresentation, isAgreementEnded } from '../agreementStatus'

const WARNING_PILL = 'border-warning/200 bg-warning/50 text-warning/700'
const ERROR_PILL = 'border-error/200 bg-error/50 text-error/700'

const AWAITING_CLIK = {
  label: 'Waiting CLIK approval',
  cls: WARNING_PILL,
}

// `approved` is NOT a waiting state and must never share a label with one.
// The reviewer's verdict is already in; what is left is the tenant's own move —
// enter the CLIK credentials, which is the only thing that flips the company to
// active (backoffice-service validateCredential → markVerificationActive).
// Amber would say "sit tight"; brand blue says "your turn".
const NEEDS_CLIK_CREDENTIAL = {
  label: 'CLIK activation required',
  cls: 'border-brand/200 bg-brand/50 text-brand/700',
}

export const AVATAR_BADGE = {
  waiting_approval: AWAITING_CLIK,
  completed: AWAITING_CLIK,
  approved: NEEDS_CLIK_CREDENTIAL,
  rejected: {
    label: 'Registration rejected',
    cls: ERROR_PILL,
  },
  expired: {
    label: 'Expired',
    cls: 'border-gray/300 bg-gray/50 text-gray/700',
  },
}

const EMPTY_TITLE = 'Plan & saldo not available yet'

const AWAITING_COPY = {
  title: EMPTY_TITLE,
  description:
    'Your subscription plan and saldo become active once the credit bureau (CLIK) verification is complete. This usually takes 1–3 business days.',
}

export const EMPTY_STATE_COPY = {
  waiting_approval: AWAITING_COPY,
  completed: AWAITING_COPY,
  approved: {
    title: EMPTY_TITLE,
    description:
      'Your company registration has been approved. Enter your CLIK credentials on the Company profile page to activate your plan and saldo.',
  },
  rejected: {
    title: EMPTY_TITLE,
    description:
      'Your company registration was rejected. Fix the flagged documents, then resubmit to continue.',
  },
}

// ─── Approved, but the agreement is not executed yet ─────────────────────────
//
// Sequential DocuSign signing: the review's approval releases the agreement to
// the CLIK countersigner, and the CLIK credentials can only be entered once CLIK
// has signed (backoffice-service validateCredential → 409). So an approved
// company whose agreement is still out (or was declined / voided) must NOT be
// told "CLIK activation required — enter your credentials": that is the one
// thing it cannot do yet. `gate` is agreementStatus.credentialGate(company).
const AGREEMENT_PILL = { warning: WARNING_PILL, error: ERROR_PILL }

const AGREEMENT_BLOCKED_COPY = {
  awaiting_clik_signature:
    'Your company registration has been approved and the membership agreement is now with CLIK for countersignature. Once CLIK has signed it, enter your CLIK credentials on the Company profile page to activate your plan and saldo.',
  declined:
    'Your company registration has been approved, but the membership agreement was declined and can no longer be signed. Everest/CLIK will reset it so you can sign it again — you will get an email when it is ready to sign.',
  voided:
    'Your company registration has been approved, but the membership agreement was voided or has expired and can no longer be signed. Everest/CLIK will reset it so you can sign it again — you will get an email when it is ready to sign.',
}

const agreementBlocksApproved = (status, gate) => status === 'approved' && !!gate?.blocked

/** The default is the awaiting-CLIK copy: it is the only state that resolves by waiting. */
export function emptyStateCopyFor(status, gate = null) {
  if (agreementBlocksApproved(status, gate)) {
    return {
      title: EMPTY_TITLE,
      description: AGREEMENT_BLOCKED_COPY[gate.status] || gate.note,
    }
  }
  return EMPTY_STATE_COPY[status] || AWAITING_COPY
}

export function avatarBadgeFor(status, gate = null) {
  if (agreementBlocksApproved(status, gate)) {
    const agreement = agreementPresentation(gate.status)
    return { label: agreement.label, cls: AGREEMENT_PILL[agreement.tone] || WARNING_PILL }
  }
  return AVATAR_BADGE[status] || AWAITING_CLIK
}

// ─── Enrolment (wizard not finished) ────────────────────────────────────────

// `enrollment_step` is the step the company is ON, not the count of steps done:
// creating the company lands it on 2, and the wizard resumes at exactly this
// number (RegisterCompanyInfoForm reads it the same way). So 4 means every form
// is submitted and only the membership agreement is outstanding — which is why
// step 4 must never be told to "lengkapi dokumen".
const STEP_COPY = {
  1: 'Company information is incomplete. Continue the registration to fill it in.',
  2: 'Company legal documents have not been uploaded yet. Continue the registration to add the NPWP, NIB, Akta Pendirian, and Akta Perubahan Terbaru.',
  3: 'Authorized representative details and the Surat kuasa have not been filled in yet. Continue the registration to complete them.',
}

// DocuSign statuses that mean "an envelope is out there, waiting on the signer".
// `declined` and `voided` are absent on purpose — the backend's own
// findActiveEnvelope() excludes them, so signing-status answers 404 for those.
// The company's own latest agreement (`agreement`) still tells them apart from
// "never sent".
const AWAITING_SIGNATURE = ['created', 'sent', 'delivered']

// The previous agreement can no longer be signed and the company is at the
// agreement step again — the client declined it, it expired, or Everest/CLIK
// reset it (SAW reset-agreement). Step 4 sends a new envelope.
const RESIGN_COPY = {
  declined:
    'The previous membership agreement was declined and can no longer be signed. Continue the registration to sign the membership agreement again.',
  voided:
    'The previous membership agreement was voided or has expired and can no longer be signed. Continue the registration to sign the membership agreement again.',
}

/**
 * What a company that has NOT finished the wizard should see on this page.
 *
 * @param {number} step             companies.enrollment_step (1-4)
 * @param {string|null} envelope    DocuSign envelope_status, or null when the
 *                                  signing-status endpoint has no active envelope
 * @param {string|null} signerEmail who the agreement was emailed to
 * @param {string|null} agreement   the company's latest agreement_status (the
 *                                  declined / voided envelope stays the latest
 *                                  until a new one is sent)
 */
export function enrolmentPresentation({ step, envelope, signerEmail, agreement } = {}) {
  const n = Number(step) || 1

  if (n >= 4) {
    // The latest agreement is declined / voided and no new one is out yet:
    // sign again. Checked before "completed" — a signature on an envelope that
    // was voided afterwards (e.g. CLIK never countersigned, then reset) does
    // not count any more.
    if (isAgreementEnded(agreement) && !AWAITING_SIGNATURE.includes(envelope)) {
      return {
        badge: { label: 'Sign again', cls: WARNING_PILL },
        title: 'Please sign the agreement again',
        description: RESIGN_COPY[agreement],
        canResend: false,
        continueLabel: 'Sign the agreement again',
      }
    }
    // signing-status answers `completed` as soon as the CLIENT has signed (the
    // envelope itself then waits for the CLIK review + countersignature). The
    // company's own enrolment fields can lag behind that (session seed read
    // before the signature landed) — never offer to sign a signed agreement.
    if (envelope === 'completed') {
      return {
        badge: { label: agreementPresentation('awaiting_review').label, cls: WARNING_PILL },
        title: 'Agreement signed',
        description:
          'You have signed the membership agreement. CLIK will now review your application; once it is approved, CLIK countersigns the agreement and you can enter your CLIK credentials to activate the company.',
        canResend: false,
        canContinue: false,
      }
    }
    if (AWAITING_SIGNATURE.includes(envelope)) {
      return {
        badge: { label: 'Awaiting signature', cls: WARNING_PILL },
        title: 'Awaiting agreement signature',
        description: signerEmail
          ? `The membership agreement has been sent to ${signerEmail}. Open that email and sign it to complete the registration.`
          : "The membership agreement has been sent to the authorized representative's email. Open that email and sign it to complete the registration.",
        canResend: true,
      }
    }
    // No active envelope: either it was never sent, or the signer declined and
    // the backend dropped it from the active set. Both resolve the same way —
    // go back into the wizard and (re)send the agreement.
    return {
      badge: { label: 'Ready to sign', cls: WARNING_PILL },
      title: 'Ready to sign the agreement',
      description:
        'Your company data and documents are complete. Accept and sign the membership agreement to complete the registration.',
      canResend: false,
    }
  }

  return {
    badge: { label: 'Registration incomplete', cls: WARNING_PILL },
    title: 'Company registration incomplete',
    description: STEP_COPY[n] || STEP_COPY[1],
    canResend: false,
  }
}
